<template>
  <b-container fluid class="">
    <h1 class="m-3">
      {{ title }}
    </h1>

    <commonActions :data-ready="dataReadyTables" :filename-prefix="title" :use-time-filter="false"
      :use-picker-filter="!choiceInput" :use-month-only="choiceInput" :date-picker-format="datePickerFormat"
      :multiple="choiceInput" :use-month-label="choiceInput" @commonFiltersUpdated="commonFiltersUpdated($event)"
      @clickDownloadPdf="downloadPdf($event)" @clickDownloadExcel="downloadExcel($event)"
      @clickDownloadCsv="downloadCsv($event)" />

    <div class="row tbl-legend">
      <small class="text-muted">{{ legend }}</small>
    </div>

    <div class="table-header">{{ tableTitle }}
      <div v-if="$store.getters['authentication/getRoleRank'] <= 1" v-on:click="inputChoiceChange"
        class="custom-toggle-container">
        <span v-b-tooltip.hover="{ title: this.$t('AzureCost.legendGlobal'), customClass: 'tooltip-w-2x' }"
          :class="['toggle-label', { active: choiceInput }]">{{ this.$t("AzureCost.inputChoiceMonth") }}</span>
        <b-form-checkbox v-model="choiceInput" switch class="custom-switch" :aria-checked="choiceInput"
          style="display: none;"></b-form-checkbox>
        <span v-b-tooltip.hover="{ title: this.$t('AzureCost.legendSite'), customClass: 'tooltip-w-2x' }"
          :class="['toggle-label', { active: !choiceInput }]">{{ this.$t("AzureCost.inputChoiceSite") }}</span>
      </div>
    </div>

    <table-renderer v-if="choiceInput" key="month-table" :data-table="tableData" :dimension-fields="dimensionFields"
      :measure-fields="measureFields" :data-ready="dataReadyTables" :totals-data="totalsData" ref="tableRenderer" />

    <table-renderer v-if="!choiceInput" key="site-table" :data-table="tableData" :dimension-fields="dimensionFields"
      :measure-fields="measureFields" :data-ready="dataReadyTables" :totals-data="totalsDataMonth"
      ref="tableRenderer" />

  </b-container>
</template>

<script>
import Tools from "@/utils/tools";
import TableTools from "@/utils/tableTools";
import paths from "@/router/pathConstants";
import TableRenderer from "@/components/TableRenderer.vue";
import commonActions from "@/components/pages/commonActions";
import dimensionEnum from "@/model/dimensionEnum";
import mesureEnum from "@/model/mesureEnum";
import { dateFormatEnum } from "@/filter/dateFormat";
import { AzureCost } from "@/router/routeNames";
import moment from "moment";
import abortMixin from "@/mixins/abortMixin";
import _ from "lodash";

export default {
  name: AzureCost,
  mixins: [abortMixin],
  components: {
    TableRenderer,
    commonActions,
  },
  data() {
    return {
      commonFilters: [],
      timeDimension: null,
      siteValues: [],
      datePickerFormat: dateFormatEnum.DATE_SHORT_FORMAT,
      dataReadyTables: false,
      choiceInput: false,
      tableData: [],
      dimensionFields: [],
      measureFields: [],
      groupHeaders: [],
    };
  },
  created() {
    this.choiceInput = this.$store.getters['authentication/getRole'] === 'AdminMba';
  },
  mounted() {
    this.initDisplayTable();
  },
  computed: {
    title() {
      return this.$t("title.azure_cost");
    },
    tableTitle() {
      return this.choiceInput
        ? this.$t("AzureCost.tableTitle") + this.$t("AzureCost.tableTitleSite") + " " + this.groupHeaders[0]?.label
        : this.$t("AzureCost.tableTitle") + this.$t("AzureCost.tableTitleMonth") + this.siteValues[0]?.label + " " + this.groupHeaders[0]?.label;
    },
    legend() {
      return this.$t("AzureCost.legend");
    },
    dateRangeToForce() {
      const lastMonth =
        Tools.BusinessDateHelper.getTodaysBusinessDate().subtract(1, "months");
      return this.choiceInput ? [
        lastMonth.startOf("month").format(this.datePickerFormat),
        lastMonth.endOf("month").format(this.datePickerFormat),
      ] : [
        lastMonth.startOf("month").format(this.datePickerFormat),
        lastMonth.endOf("month").format(this.datePickerFormat),
      ];
    },
    totalsData() {
      const rows = [...this.tableData];

      const totalElisa = rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_ELISA] || 0), 0);
      const totalTransactions = rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_TRANSACTION_COUNT] || 0), 0);
      const totalEquivalentTransaction = parseFloat((rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION] || 0), 0)).toFixed(2));

      const totalRow = {
        [dimensionEnum.SITE_SITE_CATEGORY_NAME]: '',
        [mesureEnum.AZURECOST_ELISA]: totalElisa,
        [mesureEnum.AZURECOST_TRANSACTION_COUNT]: totalTransactions,
        [mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION]: totalEquivalentTransaction,
      };
      return totalRow;
    },
    totalsDataMonth() {
      const rows = [...this.tableData];

      const totalElisa = rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_ELISA] || 0), 0);
      const totalTransactions = rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_TRANSACTION_COUNT] || 0), 0);
      const totalEquivalentTransactionCount = parseFloat((rows.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION] || 0), 0)).toFixed(2));

      const totalRow = {
        [mesureEnum.AZURECOST_ELISA]: totalElisa,
        [mesureEnum.AZURECOST_TRANSACTION_COUNT]: totalTransactions,
        [mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION]: totalEquivalentTransactionCount,
      };
      return totalRow;
    },
    legendToggle() {
      return this.choiceInput ? this.$t("AzureCost.legendGlobal") : this.$t("AzureCost.legendSite");
    },
  },
  methods: {
    async updateTable() {
      try {
        const { signal } = this.resetAbortController();
        this.dataReadyTables = false;
        this.tableData = [];
        var queries = [];

        const dimensionCommon = [
          dimensionEnum.SITE,
        ];

        // Requête séparée pour récupérer le nombre de transaction apr site et passer par les pré-agg
        queries.push({
          query: {
            measures: [mesureEnum.GAMEHISTORY_COUNT],
            dimensions: [
              dimensionEnum.SITE,
            ],
            timeDimensions: this.timeDimension
              ? [{ ...this.timeDimension, dimension: dimensionEnum.GAMEHISTORY_DATETIME }]
              : [],
            filters: [
              ...this.commonFilters
            ]
          },
        });

        // Reste de la requête 
        queries.push({
          query: {
            measures: [
              mesureEnum.SMARTTOUCH_COUNT
            ],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: dimensionCommon.concat(this.choiceInput ? [
              dimensionEnum.SITE_NAME,
              dimensionEnum.SITE_SITE_CATEGORY_NAME
            ] : [
              dimensionEnum.DATE_MONTH,
              dimensionEnum.DATE_YEAR
            ]),
            filters: [
              ...this.commonFilters
            ]
          }
        });

        let resultSets = await TableTools.Helper.doMultipleRequest(queries, {
          signal,
        });

        var mergedResults = TableTools.Helper.mergeResultsByCommonDimensions(
          resultSets,
          dimensionCommon
        );

        // Requête complètement séparée pour AzureCost
        let azureResultSet = [];
        try {
          const azureQuery = {
            measures: [],
            timeDimensions: this.timeDimension ? [this.timeDimension] : [],
            dimensions: [
              dimensionEnum.AZURECOST_COST,
              dimensionEnum.AZURECOST_DATE
            ],
            filters: [
              ...this.commonFilters
            ]
          };
          azureResultSet = await TableTools.Helper.doRequest(azureQuery, { signal }) || [];
        } catch (error) {
          console.error("Erreur lors de la récupération des données AzureCost:", error);
        }

        //remplissage des données du tableau
        if (this.choiceInput) {
          // Requête sans mesure pour afficher les sites sans données
          const queryNoMeasure = {
            measures: [],
            dimensions: [
              dimensionEnum.SITE_NAME,
              dimensionEnum.SITE_SITE_CATEGORY_NAME
            ]
          };
          const resultSetNoMeasure = await TableTools.Helper.doRequest(queryNoMeasure, { signal }) || [];

          // Attribution des valeurs de mesures à chaque site
          const measuresMap = new Map();
          mergedResults.forEach(row => {
            measuresMap.set(row[dimensionEnum.SITE_NAME], {
              [mesureEnum.AZURECOST_ELISA]: row[mesureEnum.SMARTTOUCH_COUNT] || 0,
              [mesureEnum.AZURECOST_TRANSACTION_COUNT]: row[mesureEnum.GAMEHISTORY_COUNT] || 0
            });
          });

          this.tableData = resultSetNoMeasure.map(row => {
            const measures = measuresMap.get(row[dimensionEnum.SITE_NAME]) || {
              [mesureEnum.AZURECOST_ELISA]: 0,
              [mesureEnum.AZURECOST_TRANSACTION_COUNT]: 0
            };

            return {
              [dimensionEnum.SITE_NAME]: row[dimensionEnum.SITE_NAME],
              [dimensionEnum.SITE_SITE_CATEGORY_NAME]: row[dimensionEnum.SITE_SITE_CATEGORY_NAME],
              [mesureEnum.AZURECOST_ELISA]: measures[mesureEnum.AZURECOST_ELISA],
              [mesureEnum.AZURECOST_TRANSACTION_COUNT]: measures[mesureEnum.AZURECOST_TRANSACTION_COUNT],
              [mesureEnum.AZURECOST_PERCENT_TRANSACTION]: 0,
              [mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION]: 0
            };
          });

          // Récupération du coût Azure total
          const azureCost = azureResultSet.length > 0 ? azureResultSet[0][dimensionEnum.AZURECOST_COST] : 0;
          const azureCostValue = parseFloat(azureCost) || 0;

          // Calculer le total des transactions de jetons dépensées pour TOUS les sites
          const totalTransac = this.tableData.reduce((sum, row) => sum + (row[mesureEnum.AZURECOST_TRANSACTION_COUNT] || 0), 0);

          // Calculer les pourcentages et les coûts équivalents
          if (azureCostValue > 0 && totalTransac > 0) {
            const transactionValues = this.tableData.map(item => {
              const transactions = item[mesureEnum.AZURECOST_TRANSACTION_COUNT] || 0;
              const percentTransac = totalTransac > 0 ? (transactions / totalTransac) * 100 : 0;
              item[mesureEnum.AZURECOST_PERCENT_TRANSACTION] = percentTransac;
              return parseFloat((azureCostValue * percentTransac / 100).toFixed(2));
            });

            // Ajuster la valeur max avec la différence d'arrondi pour garantir que le total correspond exactement
            const transactionTotal = transactionValues.reduce((sum, val) => sum + val, 0);
            const diffTransac = parseFloat((azureCostValue - transactionTotal).toFixed(2));
            if (diffTransac !== 0 && transactionValues.length > 0) {
              const maxIndexTransac = transactionValues.indexOf(Math.max(...transactionValues));
              transactionValues[maxIndexTransac] = parseFloat((transactionValues[maxIndexTransac] + diffTransac).toFixed(2));
            }

            // Appliquer les valeurs calculées
            this.tableData.forEach((item, index) => {
              item[mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION] = transactionValues[index] || 0;
            });
          } else {
            // Si pas de coût Azure ou pas d'unités, initialiser les pourcentages à 0
            this.tableData.forEach(item => {
              item[mesureEnum.AZURECOST_PERCENT_TRANSACTION] = 0;
              item[mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION] = 0;
            });
          }
        } else {
          // Préparer les coûts Azure par mois/année
          const azureCostsByMonth = {};
          azureResultSet.forEach(azure => {
            const month = moment(azure[dimensionEnum.AZURECOST_DATE]).month() + 1;
            const year = moment(azure[dimensionEnum.AZURECOST_DATE]).year();
            const key = `${year}-${month}`;

            if (!azureCostsByMonth[key]) {
              azureCostsByMonth[key] = 0;
            }
            azureCostsByMonth[key] += azure[dimensionEnum.AZURECOST_COST] || 0;
          });

          // Calculer le total des unités dépensées par mois pour TOUS les sites (pour la répartition proportionnelle)
          const totalTransacByMonth = {};
          mergedResults.forEach(row => {
            const key = `${row[dimensionEnum.DATE_YEAR]}-${row[dimensionEnum.DATE_MONTH]}`;
            if (!totalTransacByMonth[key]) {
              totalTransacByMonth[key] = 0;
            }
            totalTransacByMonth[key] += row[mesureEnum.GAMEHISTORY_COUNT] || 0;
          });

          // Filtrer pour ne garder que le site sélectionné pour l'affichage
          const site = this.siteValues && this.siteValues.length > 0 ? this.siteValues[0] : null;
          const filteredResultSet = site
            ? mergedResults.filter(row => row[dimensionEnum.SITE] === site.id)
            : mergedResults;

          // Remplissage des données du tableau avec calculs proportionnels
          this.tableData = filteredResultSet.map(row => {
            const key = `${row[dimensionEnum.DATE_YEAR]}-${row[dimensionEnum.DATE_MONTH]}`;
            const azureCostForMonth = azureCostsByMonth[key] || 0;
            const totalTransacAllSitesForMonth = totalTransacByMonth[key] || 0; // Total de TOUS les sites
            const rowTransac = row[mesureEnum.GAMEHISTORY_COUNT] || 0;

            // Calculer la part proportionnelle du coût Azure en tenant compte de TOUS les sites
            let proportionalAzureCostTransac = 0;
            let percentOfTotalTransac = 0;
            if (totalTransacAllSitesForMonth > 0 && azureCostForMonth > 0) {
              percentOfTotalTransac = (rowTransac / totalTransacAllSitesForMonth) * 100;
              proportionalAzureCostTransac = (rowTransac / totalTransacAllSitesForMonth) * azureCostForMonth;
            }

            return {
              [dimensionEnum.DATE_MONTH]: moment(`${row[dimensionEnum.DATE_YEAR]}-${row[dimensionEnum.DATE_MONTH]}-01`).format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase()),
              [mesureEnum.AZURECOST_ELISA]: row[mesureEnum.SMARTTOUCH_COUNT] || 0,
              [mesureEnum.AZURECOST_TRANSACTION_COUNT]: row[mesureEnum.GAMEHISTORY_COUNT] || 0,
              [mesureEnum.AZURECOST_PERCENT_TRANSACTION]: percentOfTotalTransac,
              [mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION]: parseFloat(proportionalAzureCostTransac.toFixed(2)),
              [dimensionEnum.DATE]: moment(`${row[dimensionEnum.DATE_YEAR]}-${row[dimensionEnum.DATE_MONTH]}`),
            };
          });

          // Trier par date
          this.tableData = TableTools.Helper.sortDatatableByDate(
            this.tableData,
            dimensionEnum.DATE
          );
        }

        this.measureFields = TableTools.Helper.constructColumnTable([
          mesureEnum.AZURECOST_ELISA,
          mesureEnum.AZURECOST_TRANSACTION_COUNT,
          this.choiceInput ? mesureEnum.AZURECOST_PERCENT_TRANSACTION : null,
          mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION
        ].filter(Boolean), null, null, true, null);

        this.dataReadyTables = true;
      } catch (error) {
        if (this.isAbortError(error)) return;
        console.error("Error updating table list:", error);
      }
    },
    initDisplayTable() {
      if (this.choiceInput) {

        var firstpart = TableTools.Helper.constructColumnTable([
          dimensionEnum.SITE_NAME,
        ]);
        var secondpart = TableTools.Helper.constructColumnTable([
          dimensionEnum.SITE_SITE_CATEGORY_NAME,
        ], null, null, true);

        this.dimensionFields = firstpart.concat(secondpart);
      } else {
        this.dimensionFields = TableTools.Helper.constructColumnTable([
          dimensionEnum.DATE_MONTH,
        ], null, null, true);
      }
      this.measureFields = TableTools.Helper.constructColumnTable([
        mesureEnum.AZURECOST_ELISA,
        mesureEnum.AZURECOST_TRANSACTION_COUNT,
        mesureEnum.AZURECOST_PERCENT_TRANSACTION,
        mesureEnum.AZURECOST_EQUIVALENT_TRANSACTION
      ], null, null, true);
      this.groupHeaders = [
        { nbOfMeasures: 4, label: "" }
      ];
    },
    commonFiltersUpdated(value) {
      this.commonFilters = value.filters;
      this.timeDimension = value.timeDimension || null;
      this.siteValues = value.siteValues;
      if (this.timeDimension) {
        this.updateGroupHeaderLabel(this.timeDimension.dateRange);
      }
      this.updateTable();
    },
    updateGroupHeaderLabel(value) {
      this.groupHeaders[0].label = this.$t("filter.period_full", {
        0: moment(value[0]).format(this.datePickerFormat),
        1: moment(value[1]).format(this.datePickerFormat)
      });
    },
    inputChoiceChange() {
      this.choiceInput = !this.choiceInput;
      this.initDisplayTable();
      this.commonFiltersUpdated({
        filters: this.commonFilters,
        timeDimension: this.timeDimension,
        siteValues: this.siteValues
      });
    },
    downloadPdf(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_PDF, "_blank");
      }
    },
    downloadExcel(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_EXCEL, "_blank");
      }
    },
    downloadCsv(data) {
      if (this.dataReadyTables) {
        this.storeExportData(data.filename, data.subtitles);
        window.open(paths.EXPORT_CSV, "_blank");
      }
    },
    storeExportData(filename, subtitles) {
      var datasForExport = [];
      const renderer = this.$refs.tableRenderer;
      const sortKey = renderer?.sortedColumn || null;

      var transformTableData = this.tableData.map(item => ({ ...item }));
      var measureFieldsExport = this.choiceInput
        ? this.measureFields.filter(field => field.key !== mesureEnum.AZURECOST_PERCENT_TRANSACTION)
        : this.measureFields;

      datasForExport.push(
        TableTools.Helper.assembleExportDataTable(
          transformTableData,
          this.dimensionFields,
          measureFieldsExport,
          null,
          this.tableTitle,
          sortKey,
          null,
          this.choiceInput ? this.totalsData : this.totalsDataMonth,
          null,
          "table-width-xl export-xxl"
        )
      );

      TableTools.Helper.storeExportData(
        datasForExport,
        this.title,
        subtitles,
        filename,
        null,
        true
      );
    },
  },
};
</script>
<style scoped>
.table-header {
  margin-right: 17px;
  margin-left: 17px;
  margin-bottom: -14px;
  padding: 8px 3px;
  display: flex;
  justify-content: space-between;
  background-color: #0118b2;
  align-items: center;
  color: white;
  font-weight: bold;
  font-family: Calibri, sans-serif;
}

.custom-toggle-container {
  display: flex;
  align-items: center;
  background: #e9ecef;
  border-radius: 18px;
  justify-content: space-between;
  width: 145px;
  cursor: pointer;
}

.toggle-label {
  padding: 0 15px;
  color: #6c757d;
  font-weight: 500;
  transition: color 0.2s, font-weight 0.2s;
}

.toggle-label.active {
  background: #fff;
  border: 1px solid #ced4da;
  font-weight: 700;
  border-radius: 13px;
}
</style>
